import { useEffect, useMemo, useRef, useState } from 'react'
import queryClient from 'api/queryClient'
import { withDepth } from 'components/filters/useStreamFilters'
import { ordinalsStreamQueryFilter } from 'components/panels/ordinalsThreshold'
import { serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import { subscribeOrdinalStreamUpdates } from 'state/ordinalStreamUpdates'
import type { PanelState } from 'state/PanelState'
import {
    MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP,
    staleLensOrdinalCount,
    unresolvedWalletOrdinalCount,
} from './ordinalStreamUpdatePolicy'
import type { OrdinalStreamQueryData } from './ordinalStreamUpdatePolicy'

export default function useOrdinalStreamUpdates(panel: PanelState): number {
    const filter = panel.filter
    const eligible = Boolean(filter?.ordinals || filter?.user_ordinals)
    const queryKey = useMemo(() => {
        if (!eligible || !filter) return null
        const effectiveFilter = ordinalsStreamQueryFilter(withDepth(filter))
        return ['ordinalsMediaStream', serializeFilterKey(effectiveFilter)] as const
    }, [eligible, filter])
    const queryKeySignature = useMemo(
        () => (queryKey ? JSON.stringify(queryKey) : null),
        [queryKey],
    )
    const queryKeyRef = useRef(queryKey)
    queryKeyRef.current = queryKey
    const [updateCount, setUpdateCount] = useState(0)
    const [walletUpdateCount, setWalletUpdateCount] = useState(0)
    const [lensStaleCount, setLensStaleCount] = useState(0)
    const updateGenerationRef = useRef(0)
    const fetchGenerationRef = useRef(0)
    const fetchCanClearUpdatesRef = useRef(false)

    useEffect(() => {
        updateGenerationRef.current = 0
        fetchGenerationRef.current = 0
        fetchCanClearUpdatesRef.current = false
        setUpdateCount(0)
        setWalletUpdateCount(0)
        setLensStaleCount(0)
        if (!eligible || !queryKeySignature) return

        return subscribeOrdinalStreamUpdates(() => {
            updateGenerationRef.current += 1
            // Keep one extra value so the pill can distinguish 20 from 20+.
            setUpdateCount(current =>
                Math.min(current + 1, MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP + 1),
            )
        })
    }, [eligible, queryKeySignature])

    // Clicking the shared floating control invalidates this exact query. Keep
    // the pill visible through the request and clear it only after fresh rank
    // data has successfully replaced the visible result.
    useEffect(() => {
        if (!eligible || !queryKeySignature) return

        const initialData = queryClient.getQueryData(queryKeyRef.current as readonly unknown[]) as
            | OrdinalStreamQueryData
            | undefined
        setWalletUpdateCount(unresolvedWalletOrdinalCount(initialData))
        setLensStaleCount(staleLensOrdinalCount(initialData))

        const unsubscribe = queryClient.getQueryCache().subscribe(event => {
            if (event?.type !== 'updated') return
            const action = (event as { action?: { type?: string; manual?: boolean } }).action
            const updatedQuery = (
                event as {
                    query?: {
                        queryKey?: unknown
                        state?: {
                            data?: OrdinalStreamQueryData
                            fetchMeta?: { fetchMore?: { direction?: unknown } }
                        }
                    }
                }
            ).query
            const updatedKey = updatedQuery?.queryKey
            if (!updatedKey || JSON.stringify(updatedKey) !== queryKeySignature) return

            if (action?.type === 'fetch') {
                // Infinite-query pagination updates this same cache entry but
                // does not refresh the ranked head the pill represents.
                fetchCanClearUpdatesRef.current =
                    updatedQuery?.state?.fetchMeta?.fetchMore?.direction == null
                fetchGenerationRef.current = updateGenerationRef.current
                return
            }
            if (action?.type === 'error') {
                fetchCanClearUpdatesRef.current = false
                return
            }
            if (action?.type !== 'success') return
            // Live state patches arrive as manual writes and can retire a row
            // from this lens, so the derived counts are recomputed either way.
            setWalletUpdateCount(unresolvedWalletOrdinalCount(updatedQuery?.state?.data))
            setLensStaleCount(staleLensOrdinalCount(updatedQuery?.state?.data))
            // Only a network response proves that the ranked head itself was
            // refreshed, which is what clears the pill.
            if (action.manual) return
            const fetchCanClearUpdates = fetchCanClearUpdatesRef.current
            fetchCanClearUpdatesRef.current = false
            if (!fetchCanClearUpdates) return
            // A request already in flight when the upvalue arrived may still
            // contain the old rank. Only a fetch started after the latest
            // signal is allowed to consume the pill.
            if (fetchGenerationRef.current < updateGenerationRef.current) return
            setUpdateCount(0)
        })
        return unsubscribe
    }, [eligible, queryKeySignature])

    return Math.min(
        updateCount + walletUpdateCount + lensStaleCount,
        MAX_ORDINAL_UPDATE_COUNT_BEFORE_CAP + 1,
    )
}
